import { useState } from "react";
import { useQuery } from "react-query";
import { apiService } from "../services/api";
import Pagination from "../components/Pagination";
import LoadingSpinner from "../components/LoadingSpinner";
import Tooltip from "../components/Tooltip";

interface Invoice {
  id: number;
  date: string;
  costType: string;
  item: string;
  costCenter: string;
  cost: number;
  netCost: number;
  invoiceCost: number;
}

export default function BillingPage() {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [costCenter, setCostCenter] = useState("");
  const [costType, setCostType] = useState("");
  const [item, setItem] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const { data, isLoading } = useQuery(
    [
      "invoices",
      month,
      costCenter,
      costType,
      item,
      search,
      currentPage,
      itemsPerPage,
    ],
    () =>
      apiService.getInvoices({
        month: month || undefined,
        costCenter: costCenter || undefined,
        costType: costType || undefined,
        item: item || undefined,
        search: search || undefined,
        page: currentPage,
        limit: itemsPerPage,
      }),
    { keepPreviousData: true },
  );

  const invoices: Invoice[] = data?.data?.data?.invoices ?? [];
  const total: number = data?.data?.data?.total ?? 0;
  const totalCost: number = data?.data?.data?.totalCost ?? 0;
  const totalInvoiceCost: number = data?.data?.data?.totalInvoiceCost ?? 0;
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Invoices
        </h1>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {total} invoice lines &middot; Total Usage:{" "}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            $
            {totalCost.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>{" "}
          &middot; Invoice Cost:{" "}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            $
            {totalInvoiceCost.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        This page shows your own Claude usage and licence lines, plus the data
        for any cost centers you approve/own.
      </p>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Month
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                setCurrentPage(1);
              }}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cost Type
            </label>
            <select
              value={costType}
              onChange={(e) => {
                setCostType(e.target.value);
                setCurrentPage(1);
              }}
              className="input w-full"
            >
              <option value="">All</option>
              <option value="Repository Cost">Repository Cost</option>
              <option value="Copilot Usage">Copilot Usage</option>
              <option value="Claude Usage">Claude Usage</option>
              <option value="Claude Licence">Claude Licence</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Item
            </label>
            <input
              type="text"
              value={item}
              onChange={(e) => {
                setItem(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Filter by item..."
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cost Center
            </label>
            <input
              type="text"
              value={costCenter}
              onChange={(e) => {
                setCostCenter(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Filter by cost center..."
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search all fields..."
              className="input w-full"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cost Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cost Center
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <span className="flex items-center justify-end">
                      Usage
                      <Tooltip text="Total usage cost, before deducting the included pool in your GitHub Copilot Business or Enterprise license ($19 or $39)." />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <span className="flex items-center justify-end">
                      Net Cost
                      <Tooltip text="Total usage cost, after deducting the included pool in your GitHub Copilot Business or Enterprise license." />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <span className="flex items-center justify-end">
                      Invoice Cost
                      <Tooltip text="What you will be invoiced this month." />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {invoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <CostTypeBadge costType={invoice.costType} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {invoice.item}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {invoice.costCenter || "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        $
                        {invoice.cost.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        $
                        {invoice.netCost.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        $
                        {invoice.invoiceCost.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(val) => {
                setItemsPerPage(val);
                setCurrentPage(1);
              }}
              totalItems={total}
            />
          )}
        </div>
      )}
    </div>
  );
}

function CostTypeBadge({ costType }: { costType: string }) {
  const colors: Record<string, string> = {
    "Repository Cost":
      "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    "Copilot Usage":
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    "Claude Usage":
      "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
    "Claude Licence":
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  };

  const colorClass =
    colors[costType] ??
    "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorClass}`}
    >
      {costType}
    </span>
  );
}
